// Capture a fixed-size design independently from its responsive preview wrapper.
// This avoids html2canvas inheriting the mobile preview's CSS transform/scale.
// @ts-ignore
import html2canvas from 'html2canvas';

const waitForImages = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(images.map((image) => {
    if (image.complete) return Promise.resolve();
    return new Promise<void>((resolve) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    });
  }));
};

export async function captureFixedDesign(element: HTMLElement, width: number, height: number): Promise<HTMLCanvasElement> {
  if (document.fonts?.ready) await document.fonts.ready;

  const stage = document.createElement('div');
  Object.assign(stage.style, {
    position: 'fixed',
    inset: '0 auto auto 0',
    width: `${width}px`,
    height: `${height}px`,
    zIndex: '-2147483647',
    pointerEvents: 'none',
    overflow: 'hidden',
  });

  const clone = element.cloneNode(true) as HTMLElement;
  Object.assign(clone.style, {
    position: 'relative',
    left: '0',
    top: '0',
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    height: `${height}px`,
    minHeight: `${height}px`,
    maxHeight: `${height}px`,
    transform: 'none',
    transformOrigin: 'top left',
    opacity: '1',
    animation: 'none',
    transition: 'none',
    margin: '0',
  });

  stage.appendChild(clone);
  document.body.appendChild(stage);

  try {
    await waitForImages(clone);
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    return await html2canvas(clone, {
      width,
      height,
      scale: 2,
      backgroundColor: null,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      windowWidth: width,
      windowHeight: height,
    });
  } finally {
    stage.remove();
  }
}
